import { useCallback, useMemo, useState } from 'react';
import agentAdminService from '@wlb/shared/api/agent/agentAdminService';
import type { AdminAgent } from '@wlb/shared/api/agent/agentAdminTypes';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { Icon } from '../components/ui/Icon';
import { IconButton, IconLink } from '../components/ui/IconButton';
import { PageHeader } from '../components/layout/PageHeader';
import { Pagination } from '../components/ui/Pagination';
import { SelectField } from '../components/ui/SelectField';
import { StatusPill } from '../components/ui/StatusPill';
import { TextField } from '../components/ui/TextField';
import { AgentFormDialog } from '../features/agents/AgentFormDialog';
import { useTriStateOptions, triStateValue } from '../features/users/triState';
import { useAsyncResource } from '../hooks/useAsyncResource';
import { formatCount, formatDate } from '../utils/format';
import { resolvePictureUrl } from '../utils/resolveImageUrl';
import '../components/ui/ui.css';
import './pages.css';

const PAGE_SIZE = 20;

/** `create` opens the form empty; a number opens it on that agent. */
type FormTarget = 'create' | number;

export function Agents() {
  const { t } = useTranslation('admin');
  const [userName, setUserName] = useState('');
  const [email, setEmail] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [joinedOn, setJoinedOn] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [formTarget, setFormTarget] = useState<FormTarget | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const debouncedUserName = useDebounce(userName);
  const debouncedEmail = useDebounce(email);
  const triStateOptions = useTriStateOptions();

  const load = useCallback(
    () =>
      agentAdminService.getAgents({
        userName: debouncedUserName.trim(),
        email: debouncedEmail.trim(),
        isActive: triStateValue(activeFilter),
        joiningDate: joinedOn,
        pageNumber,
        pageSize: PAGE_SIZE,
      }),
    [activeFilter, debouncedEmail, debouncedUserName, joinedOn, pageNumber],
  );

  const requestKey = [
    debouncedUserName,
    debouncedEmail,
    activeFilter,
    joinedOn,
    pageNumber,
  ].join('|');

  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    requestKey,
    t('common.errorTitle'),
    true,
    'agents',
  );

  const changeFilter = (apply: () => void) => {
    setPageNumber(1);
    apply();
  };

  const toggleActive = useCallback(
    async (agent: AdminAgent) => {
      setPendingId(agent.agentId);
      try {
        await agentAdminService.setAgentActive(agent.agentId, !agent.isActive);
        ToastService.show({
          type: 'success',
          message: agent.isActive
            ? t('agents.toasts.deactivated')
            : t('agents.toasts.activated'),
        });
        reload();
      } catch (cause) {
        ToastService.show({
          type: 'error',
          message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
        });
      } finally {
        setPendingId(null);
      }
    },
    [reload, t],
  );

  const columns = useMemo<readonly DataTableColumn<AdminAgent>[]>(
    () => [
      {
        key: 'agent',
        header: t('agents.columns.agent'),
        render: agent => (
          <div className="ui-table__cell-with-avatar">
            <Avatar
              src={resolvePictureUrl(agent.photo)}
              name={agent.userName || t('common.none')}
            />
            <span>
              <span className="ui-table__primary">
                {agent.userName || t('common.none')}
              </span>
              {agent.fullName ? (
                <span className="ui-table__secondary">{agent.fullName}</span>
              ) : null}
            </span>
          </div>
        ),
      },
      {
        key: 'email',
        header: t('agents.columns.email'),
        render: agent => (
          <span className="ui-table__truncate">{agent.email || t('common.none')}</span>
        ),
      },
      {
        key: 'persona',
        header: t('agents.columns.persona'),
        render: agent => agent.persona || t('common.none'),
      },
      {
        key: 'frequency',
        header: t('agents.columns.frequency'),
        render: agent =>
          t('agents.frequency.value', {
            likes: formatCount(agent.likeFrequency),
            comments: formatCount(agent.commentFrequency),
          }),
      },
      {
        key: 'activity',
        header: t('agents.columns.activity'),
        render: agent =>
          t('agents.activity.value', {
            posts: formatCount(agent.weeklyPosts),
            likes: formatCount(agent.weeklyLikes),
            comments: formatCount(agent.weeklyComments),
          }),
      },
      {
        key: 'buddies',
        header: t('agents.columns.buddies'),
        numeric: true,
        render: agent => formatCount(agent.buddiesCount),
      },
      {
        key: 'joined',
        header: t('agents.columns.joined'),
        render: agent => formatDate(agent.joiningDate) || t('common.none'),
      },
      {
        key: 'lastPost',
        header: t('agents.columns.lastPost'),
        render: agent => formatDate(agent.lastPostDate) || t('common.none'),
      },
      {
        key: 'status',
        header: t('agents.columns.status'),
        render: agent => (
          <StatusPill
            label={agent.isActive ? t('status.active') : t('status.inactive')}
            tone={agent.isActive ? 'success' : 'neutral'}
          />
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        render: agent => (
          <div className="ui-table__actions">
            <IconLink
              icon="view"
              tone="blue"
              label={t('agents.actions.view')}
              to={`/agents/${agent.agentId}`}
            />
            <IconLink
              icon="edit"
              tone="indigo"
              label={t('agents.actions.edit')}
              to={`/agents/${agent.agentId}/edit`}
            />
            <IconButton
              icon={agent.isActive ? 'ban' : 'check'}
              tone={agent.isActive ? 'rose' : 'emerald'}
              disabled={pendingId === agent.agentId}
              label={
                agent.isActive
                  ? t('agents.actions.deactivate')
                  : t('agents.actions.activate')
              }
              onClick={() => toggleActive(agent)}
            />
          </div>
        ),
      },
    ],
    [pendingId, t, toggleActive],
  );

  return (
    <div className="page page--fill">
      <PageHeader
        title={t('agents.title')}
        subtitle={t('agents.subtitle')}
        actions={
          <>
            <IconButton
              icon="refresh"
              tone="slate"
              label={t('common.refresh')}
              disabled={refreshing}
              onClick={reload}
            />
            {/* A primary action keeps its words: an unlabelled icon is fine for
                a row action an operator repeats, not for the one button that
                creates a record. */}
            <Button small onClick={() => setFormTarget('create')}>
              <Icon name="plus" size="sm" />
              {t('agents.create')}
            </Button>
          </>
        }
      />

      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field">
          <TextField
            label={t('agents.filters.userNameLabel')}
            type="search"
            placeholder={t('agents.filters.userNamePlaceholder')}
            value={userName}
            onChange={event => changeFilter(() => setUserName(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field">
          <TextField
            label={t('agents.filters.emailLabel')}
            type="search"
            placeholder={t('agents.filters.emailPlaceholder')}
            value={email}
            onChange={event => changeFilter(() => setEmail(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field">
          <SelectField
            label={t('agents.filters.statusLabel')}
            options={triStateOptions}
            value={activeFilter}
            onChange={event => changeFilter(() => setActiveFilter(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field">
          <TextField
            label={t('agents.filters.joinedOnLabel')}
            type="date"
            value={joinedOn}
            onChange={event => changeFilter(() => setJoinedOn(event.target.value))}
          />
        </div>
      </section>

      <DataTable
        caption={t('agents.title')}
        columns={columns}
        rows={data?.items ?? []}
        rowKey={agent => agent.agentId}
        status={status}
        refreshing={refreshing}
        error={error}
        onRetry={reload}
        emptyTitle={t('agents.emptyTitle')}
        emptyMessage={t('agents.emptyMessage')}
        scrollResetKey={requestKey}
        footer={
          data ? (
            <Pagination
              pageNumber={data.pageNumber}
              totalPages={data.totalPages}
              totalCount={data.totalCount}
              hasPreviousPage={data.hasPreviousPage}
              hasNextPage={data.hasNextPage}
              pageSize={PAGE_SIZE}
              onChange={setPageNumber}
            />
          ) : null
        }
      />

      {formTarget != null ? (
        <AgentFormDialog
          agentId={formTarget === 'create' ? null : formTarget}
          onClose={() => setFormTarget(null)}
          onSaved={reload}
        />
      ) : null}
    </div>
  );
}
